import React, { useEffect, useRef, useState } from "react";
import "./Tweets.css";

const TWITTER_USERNAME = "yitalife";
const WIDGET_SCRIPT_ID = "x-platform-widgets";

function loadXWidgets() {
  return new Promise((resolve, reject) => {
    if (window.twttr?.widgets?.createTimeline) {
      resolve(window.twttr);
      return;
    }

    const script = document.getElementById(WIDGET_SCRIPT_ID) || document.createElement("script");
    const finishLoading = () => {
      if (window.twttr?.widgets?.createTimeline) {
        resolve(window.twttr);
      } else {
        reject(new Error("X widgets did not initialise."));
      }
    };

    script.addEventListener("load", finishLoading, { once: true });
    script.addEventListener("error", () => reject(new Error("X widgets could not be loaded.")), { once: true });

    if (!script.parentNode) {
      script.id = WIDGET_SCRIPT_ID;
      script.src = "https://platform.twitter.com/widgets.js";
      script.async = true;
      document.body.appendChild(script);
    }
  });
}

export default function TweetEmbed() {
  const timelineRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(() => {
      if (active) setStatus("error");
    }, 10000);

    loadXWidgets()
      .then((twttr) => twttr.widgets.createTimeline(
        { sourceType: "profile", screenName: TWITTER_USERNAME },
        timelineRef.current,
        { height: 520, theme: "light", tweetLimit: 3, chrome: "nofooter" },
      ))
      .then(() => {
        if (active) setStatus("loaded");
      })
      .catch(() => {
        if (active) setStatus("error");
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <section className="tweets-section" aria-labelledby="tweets-heading">
      <div className="container">
        {/* Header */}
        <div className="tweets-header" data-aos="fade-up">
          <div className="section-label">
            <ion-icon name="logo-twitter"></ion-icon>
            Social Media
          </div>
          <h2 id="tweets-heading">Featured Tweets</h2>
          <p>Follow our latest healthcare updates and community outreach directly from X (Twitter).</p>
        </div>

        <div className="row align-items-center justify-content-center g-5">
          {/* Left: Live Twitter Timeline */}
          <div className="col-lg-6 col-md-12" data-aos="fade-right">
            <div className="tweet-embed-wrapper">
              {status === "loading" && <div className="tweet-loading">Loading posts from X...</div>}
              <div ref={timelineRef} className={status === "loading" ? "tweet-timeline pending" : "tweet-timeline"} />
              {status === "error" && (
                <div className="tweet-fallback" role="status">
                  <p>Posts from X could not load in this browser.</p>
                  <a href={`https://x.com/${TWITTER_USERNAME}`} target="_blank" rel="noreferrer">
                    View @{TWITTER_USERNAME} on X
                    <ion-icon name="open-outline"></ion-icon>
                  </a>
                </div>
              )}
            </div>

            <div className="twitter-cta">
              <a
                className="twitter-cta-btn"
                href={`https://x.com/${TWITTER_USERNAME}`}
                target="_blank"
                rel="noreferrer"
              >
                <ion-icon name="logo-twitter"></ion-icon>
                Follow @{TWITTER_USERNAME}
              </a>
            </div>
          </div>

          {/* Right: Info + Social Links */}
          <div className="col-lg-5 col-md-10" data-aos="fade-left" data-aos-delay="100">
            <div className="tweets-info-card">
              <div>
                <h3 className="tweets-info-heading">Stay Connected with Yita Life</h3>
                <p className="tweets-info-desc">
                  We regularly share health tips, community outreach updates, and
                  important healthcare news. Follow us to stay informed and
                  engaged with quality healthcare conversations.
                </p>
              </div>

              <div>
                <p style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "14px", fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  FIND US ON SOCIAL MEDIA
                </p>
                <div className="tweets-social-links">
                  <a
                    href={`https://x.com/${TWITTER_USERNAME}`}
                    target="_blank"
                    rel="noreferrer"
                    className="tweet-social-link twitter"
                  >
                    <ion-icon name="logo-twitter"></ion-icon>
                    Twitter / X
                  </a>
                  <a
                    href="https://www.facebook.com/yitalife.yms?mibextid=LQQJ4d"
                    target="_blank"
                    rel="noreferrer"
                    className="tweet-social-link facebook"
                  >
                    <ion-icon name="logo-facebook"></ion-icon>
                    Facebook
                  </a>
                </div>
              </div>

              <div style={{ background: "var(--primary-light)", borderRadius: "var(--radius-sm)", padding: "16px" }}>
                <p style={{ fontSize: "14px", color: "var(--primary)", margin: 0, fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
                  <strong>📣 Community Outreach:</strong> Follow our social channels to learn about upcoming medical camps, free health screenings, and humanitarian programmes near you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
